import {
  Modal,
  Typography,
  PrimaryButton,
  SnackbarStatus,
  Checkbox,
  Export,
  StatusBar,
} from '@weareopenr/component-library';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import chefPlating from 'src/assets/images/chef-plating.png';
import styles from './ExportPriceSchemeModal.module.scss';
import { triggerPriceSchemeExport } from '../../api/triggerPriceSchemeExport/triggerPriceSchemeExport';
import { useState } from 'react';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';

interface IExportPriceSchemeModalProps {
  isOpen: boolean;
  onClose: () => void;
  priceSchemeId?: string;
}

const Content = ({ onClose, priceSchemeId }: Pick<IExportPriceSchemeModalProps, 'onClose' | 'priceSchemeId'>) => {
  const { trackEvent, captureException } = useUserTracking();
  const { errorMessage, showNotification, clearError } = useModalNotification();
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [isAcknowledged, setIsAcknowledged] = useState<boolean>(false);

  const handleDownload = async () => {
    if (isDownloading) return;
    clearError();
    try {
      setIsDownloading(true);
      await triggerPriceSchemeExport(priceSchemeId);
      trackEvent('price_scheme_exported', { priceSchemeId });
      showNotification({
        status: SnackbarStatus.PROGRESS,
        message: 'Your CSV is being generated and will be available shortly.',
      });
      onClose();
    } catch (e) {
      captureException(e as Error, { tags: { action: 'export_price_scheme', priceSchemeId } });
      showNotification({ status: SnackbarStatus.ERROR, message: 'Something went wrong when downloading the CSV' });
    } finally {
      setIsDownloading(false);
    }
  };

  const isButtonDisabled = isDownloading || (priceSchemeId && !isAcknowledged);

  return (
    <div className={styles['content']}>
      <Typography size="xxlarge" weight="bold">
        How to use the price import CSV
      </Typography>
      <Typography size="large" weight="regular">
        When you import a Price CSV, any prices listed in the file will be added as <b>manual overrides</b>. These
        overrides will replace prices that come from channel rules, price bands, or dynamic pricing.
      </Typography>

      <Typography size="large" weight="regular">
        If your price scheme uses rules and you want them to keep working as expected, only include{' '}
        <b>default prices</b> in your CSV — that way, your pricing rules will still apply automatically.
      </Typography>

      {priceSchemeId && (
        <Checkbox
          checked={isAcknowledged}
          onChange={(event) => setIsAcknowledged(event.target.checked)}
          label="I acknowledge that any changes made to the scheme after the generation of the CSV will not be captured in the file."
        />
      )}

      <PrimaryButton
        type="button"
        size="full-width"
        onClick={handleDownload}
        loading={isDownloading}
        disabled={isButtonDisabled}
      >
        Generate CSV
      </PrimaryButton>
      <div className={styles['support-section']}>
        <Typography size="medium" weight="bold">
          Get a detailed How-To Guide on mastering the Openr CSV import functionality using our support page
        </Typography>
        <Typography size="medium" weight="bold">
          <a
            href="https://weareopenr.atlassian.net/servicedesk/customer/portal/1/article/1134133251?source=shareable-url"
            target="_blank"
            rel="noopener noreferrer"
            className={styles['support-link']}
          >
            <Export size="small" className={styles['export-icon']} /> Go to CSV How-To Guide
          </a>
        </Typography>
      </div>
      <div className={styles['error-banner']}>
        {errorMessage && <StatusBar message={errorMessage} variant="error" />}
      </div>
    </div>
  );
};
export const ExportPriceSchemeModal = ({ isOpen, onClose, priceSchemeId }: IExportPriceSchemeModalProps) => {
  return (
    <Modal open={isOpen} closeModal={onClose} image={chefPlating} imageAlt={'chef plating food'}>
      <Content onClose={onClose} priceSchemeId={priceSchemeId} />
    </Modal>
  );
};
